/**
 * Stack component — shared types and Zod prop schema.
 *
 * A layout container that arranges children along a single axis
 * (vertical or horizontal) with configurable gap, padding, and alignment.
 * All spacing values reference the design system's spacing tokens.
 *
 * Stack is accessibility-transparent — it adds no role, label, or
 * focusable surface. Children retain their own semantic properties.
 */

import { z } from 'zod';
import { spacing, servedSpacingKeys, UNCARDED_TEXT_INSET } from '../../elements/spacing';
import { colors } from '../../elements/colors';
import { radius } from '../../elements/radius';

export const StackDirection = z.enum(['vertical', 'horizontal']);
export type StackDirection = z.infer<typeof StackDirection>;

/**
 * Optional surface fill for a grouping section — a closed set of neutral
 * background roles. A bare Stack stays transparent; a section that groups a set
 * of cards can opt into one of these.
 */
export const StackBackground = z.enum(['primary', 'secondary', 'container']);
export type StackBackground = z.infer<typeof StackBackground>;

export const stackBackgroundColorFor: Record<StackBackground, string> = {
  primary: colors.background.primary,
  secondary: colors.background.secondary,
  container: colors.background.container,
};

/**
 * Optional shape. `pill` turns the Stack into a fully rounded chip on the
 * primary background that hugs a short icon + label row. The fill, radius and
 * padding all live inside the option, so a build that predates it drops the
 * whole treatment and renders the plain row, never a half-styled one.
 */
export const StackShape = z.enum(['pill']);
export type StackShape = z.infer<typeof StackShape>;

/**
 * Optional role-named horizontal inset for a composite row that does not sit
 * in a card, applied as internal padding on the horizontal axis (it wins over
 * `padding` / `paddingHorizontal` there).
 * - `uncarded`: the uncarded text inset on both sides, so the row's leading
 *   content lands on the wide screen gutter.
 * - `uncardedTrailingControl`: the uncarded inset leading, and the card inset
 *   trailing, for a row that ends in a control aligned with the cards' content.
 *
 * Reserved for rows whose visible element carries its own background, where a
 * text component's own inset cannot move it. Bare text takes Text's `inset`.
 */
export const StackInset = z.enum(['uncarded', 'uncardedTrailingControl']);
export type StackInset = z.infer<typeof StackInset>;

export const SpacingKey = z.enum(['none', ...servedSpacingKeys]);
export type SpacingKey = z.infer<typeof SpacingKey>;

export const StackAlign = z.enum(['start', 'center', 'end', 'stretch']);
export type StackAlign = z.infer<typeof StackAlign>;

export const StackJustify = z.enum(['start', 'center', 'end', 'space-between']);
export type StackJustify = z.infer<typeof StackJustify>;

export const stackPropsSchema = z.object({
  direction: StackDirection.default('vertical'),
  gap: SpacingKey.default('none'),
  padding: SpacingKey.default('none'),
  // Per-axis overrides. When set, each takes precedence over the uniform
  // `padding` on that axis only; when absent, that axis falls back to `padding`.
  // Lets a container add asymmetric breathing room (e.g. more vertical than
  // horizontal) without introducing a wrapper element.
  paddingVertical: SpacingKey.optional(),
  paddingHorizontal: SpacingKey.optional(),
  align: StackAlign.default('stretch'),
  justify: StackJustify.default('start'),
  wrap: z.boolean().default(false),
  flex: z.number().nonnegative().optional(),
  // A fixed pixel width for a parent-owned column that must match another
  // element's geometry exactly (e.g. a header label matching a sibling row's
  // fixed-width section) — flex alone can only express proportional ratios.
  // Disables grow/shrink so the box never stretches or gets squeezed by
  // neighboring flex items.
  width: z.number().positive().optional(),
  /**
   * Optional neutral surface fill for a grouping section (default: transparent).
   * The parent remains responsible for the Stack's external placement.
   */
  background: StackBackground.optional(),
  /**
   * Full-bleed to the screen gutter: cancels it with a negative root margin so
   * a `background` fill reaches both screen edges, and re-insets the children
   * by the same amount so they stay flush with non-bleeding siblings. Both
   * halves are applied here — as in `Rail.bleed` and
   * `ScrollableWeekStrip.bleed` — so the prop is correct on its own. An
   * explicit `paddingHorizontal` still wins, for a deliberately edge-to-edge
   * child such as an image band.
   *
   * Defaults false so the root stays margin-free (ADR-020 Rule 2) — the parent
   * owns external layout, and a composition opts in only when it knows its
   * own gutter.
   *
   * Precondition: every ancestor between this Stack and its `Screen` adds no
   * horizontal padding, and the `Screen` keeps its default
   * `contentPaddingHorizontal`. The cancellation is the shared `SCREEN_GUTTER`,
   * never a measurement, so any intervening padding — a `Card`, a `Smartcard`
   * body, a padded parent Stack — or a Screen widened to `lg` for non-card
   * content (ADR-091) leaves the fill over- or under-shooting by the difference.
   */
  bleed: z.boolean().default(false),
  /** Optional shape; see `StackShape`. */
  shape: StackShape.optional(),
  /** Optional role-named horizontal inset; see `StackInset`. */
  inset: StackInset.optional(),
  testID: z.string().optional(),
});

export type StackProps = z.infer<typeof stackPropsSchema>;

/** The pill shape's fill and corner. */
export const STACK_PILL_BACKGROUND = colors.background.primary;
export const STACK_PILL_RADIUS = radius.rounded;
/** The pill's padding: a short vertical inset, and more room trailing than leading so a leading glyph sits close to the edge. */
export const STACK_PILL_PADDING_VERTICAL = spacing.xs;
export const STACK_PILL_PADDING_LEADING = spacing.s;
export const STACK_PILL_PADDING_TRAILING = spacing.sm;

/** The card inset a trailing control aligns with. */
const STACK_INSET_TRAILING_CONTROL = spacing.m;

/** Resolve a StackInset to its internal leading and trailing padding. */
export function stackInsetPadding(inset: StackInset): { leading: number; trailing: number } {
  return inset === 'uncarded'
    ? { leading: UNCARDED_TEXT_INSET, trailing: UNCARDED_TEXT_INSET }
    : { leading: UNCARDED_TEXT_INSET, trailing: STACK_INSET_TRAILING_CONTROL };
}

/** Resolve a SpacingKey to its pixel value. */
export function spacingValue(key: SpacingKey): number {
  if (key === 'none') return 0;
  return spacing[key];
}

const ALIGN_MAP = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  stretch: 'stretch',
} as const;

const JUSTIFY_MAP = {
  start: 'flex-start',
  center: 'center',
  end: 'flex-end',
  'space-between': 'space-between',
} as const;

/** Resolve StackAlign to a flexbox alignItems value. */
export function alignToFlex(align: StackAlign): (typeof ALIGN_MAP)[StackAlign] {
  return ALIGN_MAP[align];
}

/** Resolve StackJustify to a flexbox justifyContent value. */
export function justifyToFlex(justify: StackJustify): (typeof JUSTIFY_MAP)[StackJustify] {
  return JUSTIFY_MAP[justify];
}
